/**
 * Centralised SEO configuration for TeeSync
 * All SEO-related constants and templates in one place
 */

export const seoConfig = {
  siteName: 'TeeSync',
  siteUrl: 'https://teesync.co.uk',
  defaultTitle: 'TeeSync - Golf Scheduling Made Easy',
  defaultDescription: "Plan rounds for when you're free and the conditions are good. TeeSync syncs availability with friends, checks weather, and shows live tee times.",
  titleTemplate: '%s | TeeSync',
  twitterHandle: '@teesync',
  ogImage: 'https://teesync.co.uk/og-image.png',
  locale: 'en_GB',
  contactEmail: 'hello@teesync.co.uk',
}

/**
 * Page-specific title templates
 * Use functions for dynamic titles, strings for static ones
 */
export const pageTitles = {
  // Public marketing pages
  home: 'TeeSync - Play More Golf. Coordinate Less.',
  features: 'Features - Weather, Availability & Smart Scheduling | TeeSync',
  howItWorks: 'How TeeSync Works - From Availability to Tee Time in 3 Steps | TeeSync',
  pricing: 'Pricing - Free Forever, Pro from £2.49/mo | TeeSync',
  integrations: 'Integrations - Outlook, Google Calendar & Tee Times | TeeSync',

  // Auth pages
  login: 'Sign In | TeeSync',
  signup: 'Create Account | TeeSync',
  forgotPassword: 'Reset Password | TeeSync',
  resetPassword: 'Set New Password | TeeSync',
  emailConfirmation: 'Confirm Email | TeeSync',

  // Static pages
  privacy: 'Privacy Policy | TeeSync',
  support: 'Support | TeeSync',
  knowledge: 'Knowledge Base | TeeSync',
  notFound: 'Page Not Found | TeeSync',

  // Protected pages (noindex)
  dashboard: 'Dashboard | TeeSync',
  groups: 'My Groups | TeeSync',
  rounds: 'My Rounds | TeeSync',
  members: 'Members | TeeSync',
  settings: 'Settings | TeeSync',
  profile: 'Profile | TeeSync',
  availability: 'My Availability | TeeSync',
  notifications: 'Notifications | TeeSync',
  admin: 'Admin Dashboard | TeeSync',
}

/**
 * Page-specific description templates
 * Keep descriptions between 150-160 characters for optimal display
 */
export const pageDescriptions = {
  // Public marketing pages
  home: "TeeSync finds tee times when you and your group are free and the weather's right. For regular foursomes and solo players. Free to start.",
  features: 'Calendar-synced availability, weather-scored tee times and live tee-time lookup across 20,000+ courses - all in one app.',
  howItWorks: 'Set your availability on a daily timeline, pick a group or play solo, and TeeSync surfaces tee times that actually work. Five minutes to first round.',
  pricing: 'TeeSync is free to use. Upgrade to Pro for unlimited groups and friends, full calendar sync and unlimited Smart Schedule runs.',
  integrations: 'Two-way sync with Outlook and Google Calendar, live tee times from 20,000+ courses, plus weather scoring built in.',

  // Auth pages
  login: 'Sign in to TeeSync to plan rounds with friends, share availability, and find live tee times.',
  signup: 'Create your free TeeSync account. Plan rounds with friends, share your availability, and find live tee times.',

  // Static pages
  privacy: 'TeeSync privacy policy. Learn how we collect, use, and protect your personal information.',
  support: 'Get help with TeeSync. Contact our support team or browse frequently asked questions.',
  knowledge: 'TeeSync knowledge base. Find answers to common questions about golf scheduling and group coordination.',
}

/**
 * Per-page canonical URL paths (relative, no trailing slash). The `<SEO>`
 * component prepends `seoConfig.siteUrl`.
 */
export const pageCanonicals = {
  home: '',
  features: '/features',
  howItWorks: '/how-it-works',
  pricing: '/pricing',
  integrations: '/integrations',
  privacy: '/privacy',
  support: '/support',
  knowledge: '/knowledge',
}

/**
 * Per-page OG images. Each is 1200×630, < 200 KB, stored in `public/`.
 * Falls back to `default` for routes without a custom image.
 */
export const ogImages = {
  default: 'https://teesync.co.uk/og-image.png',
  home: 'https://teesync.co.uk/og-home.png',
  features: 'https://teesync.co.uk/og-features.png',
  howItWorks: 'https://teesync.co.uk/og-how-it-works.png',
  pricing: 'https://teesync.co.uk/og-pricing.png',
  integrations: 'https://teesync.co.uk/og-integrations.png',
}

export default seoConfig
